import {
  GripVertical,
  MoreHorizontal,
  FilePlus2,
  FolderPlus,
  Type,
  Table2,
  ImageIcon,
  Minus,
  Trash2,
  Pencil,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { DocPage, DocSection, SablonSurum } from '../types'
import { SidePanelSection } from './SidePanelSection'

type Props = {
  surum: SablonSurum
  activePageId: string
  activeSectionId?: string | null
  onSelectPage: (id: string) => void
  onSelectSection: (id: string) => void
  onAddPage: () => void
  onAddSection: () => void
  onRenameSection: (id: string) => void
  onDeleteSection: (id: string) => void
  onRenamePage: (id: string) => void
  onDeletePage: (id: string) => void
  onMoveSection: (id: string, dir: -1 | 1) => void
  onAddContent: (kind: 'text' | 'table' | 'image' | 'divider') => void
}

export function StructurePanel({
  surum,
  activePageId,
  activeSectionId,
  onSelectPage,
  onSelectSection,
  onAddPage,
  onAddSection,
  onRenameSection,
  onDeleteSection,
  onRenamePage,
  onDeletePage,
  onMoveSection,
  onAddContent,
}: Props) {
  const { t } = useTranslation()
  const pages = [...surum.pages].sort((a, b) => a.order - b.order)
  const sections = surum.sections
    .filter((s) => s.pageId === activePageId)
    .sort((a, b) => a.order - b.order)

  return (
    <div className="flex h-full min-w-0 flex-col gap-5 overflow-y-auto scrollbar-none overflow-x-hidden p-3">
      <SidePanelSection title={t('hr.belgeler.structure.pages')}>
        <div className="space-y-1.5">
          {pages.map((p) => (
            <PageThumb
              key={p.id}
              page={p}
              active={p.id === activePageId}
              onSelect={() => onSelectPage(p.id)}
              onRename={() => onRenamePage(p.id)}
              onDelete={() => onDeletePage(p.id)}
            />
          ))}
          <Button type="button" variant="outline" size="sm" className="h-8 w-full justify-start text-xs" onClick={onAddPage}>
            <FilePlus2 />
            {t('hr.belgeler.structure.addPage')}
          </Button>
        </div>
      </SidePanelSection>

      <SidePanelSection title={t('hr.belgeler.structure.sections')}>
        <div className="space-y-0.5">
          {sections.map((s, idx) => (
            <div
              key={s.id}
              className={cn(
                'flex min-w-0 items-center gap-1 rounded-lg px-1.5 py-1.5',
                activeSectionId === s.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60',
              )}
            >
              <button
                type="button"
                className="shrink-0 text-muted-foreground"
                aria-label={t('hr.belgeler.structure.reorder')}
                onClick={() => onMoveSection(s.id, idx === 0 ? 1 : -1)}
              >
                <GripVertical className="size-3.5" />
              </button>
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-xs font-medium"
                onClick={() => onSelectSection(s.id)}
              >
                {s.title}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    aria-label={t('hr.belgeler.structure.sectionMenu')}
                  >
                    <MoreHorizontal className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onRenameSection(s.id)}>
                    <Pencil className="size-3.5" />
                    {t('hr.belgeler.structure.rename')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMoveSection(s.id, -1)}>
                    {t('hr.belgeler.structure.moveUp')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onMoveSection(s.id, 1)}>
                    {t('hr.belgeler.structure.moveDown')}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive" onClick={() => onDeleteSection(s.id)}>
                    <Trash2 className="size-3.5" />
                    {t('hr.belgeler.structure.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="mt-1 h-8 w-full justify-start text-xs" onClick={onAddSection}>
            <FolderPlus />
            {t('hr.belgeler.structure.addSection')}
          </Button>
        </div>
      </SidePanelSection>

      <SidePanelSection title={t('hr.belgeler.structure.addContent')}>
        <div className="grid grid-cols-2 gap-1.5">
          <ContentBtn icon={Type} label={t('hr.belgeler.structure.textBlock')} onClick={() => onAddContent('text')} />
          <ContentBtn icon={Table2} label={t('hr.belgeler.structure.table')} onClick={() => onAddContent('table')} />
          <ContentBtn icon={ImageIcon} label={t('hr.belgeler.structure.image')} onClick={() => onAddContent('image')} />
          <ContentBtn icon={Minus} label={t('hr.belgeler.structure.divider')} onClick={() => onAddContent('divider')} />
        </div>
      </SidePanelSection>
    </div>
  )
}

function PageThumb({
  page,
  active,
  onSelect,
  onRename,
  onDelete,
}: {
  page: DocPage
  active: boolean
  onSelect: () => void
  onRename: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-card',
        active ? 'border-primary ring-1 ring-primary/25' : 'border-border/70',
      )}
    >
      <button type="button" className="block w-full p-1.5 text-left" onClick={onSelect}>
        <div className="mb-1 aspect-[210/70] rounded border border-border/50 bg-muted/40" />
        <div className="truncate text-[11px] font-semibold">{page.title}</div>
      </button>
      <div className={cn('absolute right-1 top-1 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100', active ? 'opacity-100' : 'opacity-0')}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="secondary" size="icon" className="size-6" aria-label={t('hr.belgeler.structure.pageMenu')}>
              <MoreHorizontal className="size-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onRename}>{t('hr.belgeler.structure.rename')}</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={onDelete}>
              {t('hr.belgeler.structure.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

function ContentBtn({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Type
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-w-0 flex-col items-center gap-1.5 rounded-lg border bg-card px-1.5 py-2.5 text-center text-[11px] font-medium text-foreground/80 transition-colors hover:border-primary/40 hover:bg-primary/[0.04] hover:text-primary"
    >
      <span className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="size-3.5" />
      </span>
      <span className="w-full truncate">{label}</span>
    </button>
  )
}

export type { DocSection }
